// scheduling/components/dashboard/BacklogView.jsx
// Presentational: My Backlog — the My Time (everyone) + My Jobs (JM/JP) sub-tabs,
// the Schedule chooser, and the chooser round-trip restore. Extracted from
// StaffDashboard's "backlog" tab. Data + meStaffId arrive via props; the two
// lists come from the shared backlog selectors so the badge can't drift.

import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BRAND, JOB_TYPE_COLORS, jobDept } from '../../utils/schedConstants';
import { selectMyJobsBacklog, selectMyTimeBacklog } from '../../utils/backlogSelectors';
import { computeStaffStats, utilColor, toISO } from '../../utils/staffStats';
import { useUrlState } from '../../../hooks/useUrlState';
import JobProgressBar from '../JobProgressBar';
import SchedStatBar from '../SchedStatBar';

export default function BacklogView({
  meStaffId, leadsJobs,
  jobs, jobsWIP, schedule, myTimeWIP, myTimeSched, staff,
  getDayCap, dayHoursByStaff, openChooser,
}) {
  const navigate = useNavigate();

  // Sub-tab (deeplinkable). Non-lead who deeplinks ?backlog=myjobs renders My Time
  // (render guard, not a validate dep — avoids useUrlState's async-validate trap).
  const [backlogSub, setBacklogSub] = useUrlState('backlog', 'mytime', { validate: ['mytime', 'myjobs'] });
  const effectiveSub = (backlogSub === 'myjobs' && !leadsJobs) ? 'mytime' : backlogSub;

  const [recentDays, setRecentDays] = useState(14);
  const [hidePostReview, setHidePostReview] = useState(true);
  const [barTooltip, setBarTooltip] = useState(null); // { jobId, segment }
  const [backlogHelpOpen, setBacklogHelpOpen] = useState(false);
  const backlogHelpRef = useRef(null);
  const [myTimeHelpOpen, setMyTimeHelpOpen] = useState(false);
  const myTimeHelpRef = useRef(null);

  const staffById = useMemo(() => {
    const m = {};
    for (const s of (staff || [])) m[s.StaffId] = s;
    return m;
  }, [staff]);

  const stats = useMemo(
    () => computeStaffStats({ meStaffId, schedule, getDayCap, dayHoursByStaff }),
    [meStaffId, schedule, getDayCap, dayHoursByStaff],
  );

  const backlog = useMemo(
    () => selectMyJobsBacklog({ jobs, jobsWIP, schedule, meStaffId, hidePostReview }),
    [jobs, jobsWIP, schedule, meStaffId, hidePostReview],
  );
  const myTime = useMemo(
    () => selectMyTimeBacklog({ myTimeWIP, myTimeSched, staffById, meStaffId, recentDays }),
    [myTimeWIP, myTimeSched, staffById, meStaffId, recentDays],
  );

  const myTimeJobIds = useMemo(() => new Set(myTime.map(j => j.JobId)), [myTime]);
  const myJobsJobIds = useMemo(() => new Set(backlog.map(j => j.JobId)), [backlog]);

  // Close the "?" explainers on outside click.
  useEffect(() => {
    if (!backlogHelpOpen) return undefined;
    const handler = (e) => { if (backlogHelpRef.current && !backlogHelpRef.current.contains(e.target)) setBacklogHelpOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [backlogHelpOpen]);
  useEffect(() => {
    if (!myTimeHelpOpen) return undefined;
    const handler = (e) => { if (myTimeHelpRef.current && !myTimeHelpRef.current.contains(e.target)) setMyTimeHelpOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [myTimeHelpOpen]);

  const scheduleAnotherJob = useCallback(() => {
    sessionStorage.setItem('pendingAssignment', JSON.stringify({ staffIds: [meStaffId], staffNames: [] }));
    navigate('/schedule/grid');
  }, [meStaffId, navigate]);

  return (
    <>
      <SchedStatBar items={[
        { label: 'Hours This Week', value: stats.thisWeek, color: BRAND.deepTeal },
        { label: 'Hours Next Week', value: stats.nextWeek, color: BRAND.oceanBlue },
        { label: 'Total 4-Week', value: stats.total4, color: BRAND.sageGreen },
        { label: 'Avg Utilization', value: `${stats.avgUtil}%`, color: utilColor(stats.avgUtil) },
      ]} />

      {/* Sub-tab strip (My Time / My Jobs) + recency control */}
      <div className="sched-backlog-subtabs">
        <button
          className={`sched-backlog-subtab ${effectiveSub === 'mytime' ? 'sched-backlog-subtab--active' : ''}`}
          onClick={() => setBacklogSub('mytime')}>
          My Time
          {myTime.length > 0 && <span className="sched-tab-badge sched-tab-badge--blue">{myTime.length}</span>}
        </button>
        {leadsJobs && (
          <button
            className={`sched-backlog-subtab ${effectiveSub === 'myjobs' ? 'sched-backlog-subtab--active' : ''}`}
            onClick={() => setBacklogSub('myjobs')}>
            My Jobs
            {backlog.length > 0 && <span className="sched-tab-badge sched-tab-badge--blue">{backlog.length}</span>}
          </button>
        )}
        {effectiveSub === 'mytime' && (
          <div className="sched-recency" title="Show jobs you logged time on within this many days">
            {[7, 14, 30].map(d => (
              <button key={d} className={`sched-recency-btn ${recentDays === d ? 'sched-recency-btn--on' : ''}`}
                onClick={() => setRecentDays(d)}>{d}d</button>
            ))}
          </div>
        )}
      </div>

      {/* ── My Jobs backlog (JM/JP) ── */}
      {effectiveSub === 'myjobs' && (
        <div className="sched-staff-weeks">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <label className="sched-mgr-toggle" title="Hide jobs at or past Member Review — post-review work you don't need to schedule prep hours for">
                <input type="checkbox" checked={hidePostReview} onChange={e => setHidePostReview(e.target.checked)} />
                <span className="sched-mgr-toggle-label">Hide post-review</span>
              </label>
              <span ref={backlogHelpRef} style={{ position: 'relative', display: 'inline-flex' }}>
                <button className="idd-help-btn" aria-label="Why do jobs appear in my backlog?"
                  title="Why do jobs appear here?"
                  onClick={() => setBacklogHelpOpen(o => !o)}>?</button>
                {backlogHelpOpen && (
                  <div className="sched-backlog-help-popover">
                    <div className="sched-backlog-help-title">Why a job shows here</div>
                    <p>A job appears in your backlog when all of these are true:</p>
                    <ul>
                      <li>You're the <strong>Job Manager or Job Partner</strong> on it.</li>
                      <li>It has a <strong>budget</strong> (more than 0h).</li>
                      <li>Your <strong>team has logged more than 5h</strong> on it (real work started — doesn't have to be you; your own time lives in <strong>My Time</strong>).</li>
                      <li><strong>More than 10h of budget is still unscheduled</strong> — budget minus your team's logged hours minus hours already scheduled.</li>
                      <li>It's in an <strong>active phase</strong> — past the <strong>kickoff phase</strong> and not yet in the <strong>finalization phase</strong> (phases are mapped from PE work statuses).</li>
                    </ul>
                    <p>The "Hide post-review" toggle also hides jobs at or after <strong>Member Review</strong>.</p>
                    <p className="sched-backlog-help-foot">A job leaves when you schedule the remaining hours, log enough to use up the budget, or it moves into review/finalization.</p>
                  </div>
                )}
              </span>
              <span style={{ fontSize: 12, color: '#6B7280' }}>Jobs you lead (JM/JP) that your team has started and still need scheduling.</span>
            </div>
            <button className="sched-assign-btn"
              title="Click this if you're aware of another job that doesn't show up in your backlog and want to manually schedule against it."
              style={{ fontSize: 12, padding: '7px 14px', whiteSpace: 'nowrap', flexShrink: 0 }}
              onClick={scheduleAnotherJob}>
              + Schedule Another Job
            </button>
          </div>

          {backlog.length === 0 ? (
            <div className="sched-staff-empty">
              No jobs in your backlog right now. A job shows here when you're its Job Manager or Job Partner,
              your team has logged more than 5h on it, and more than 10h of budget is still unscheduled — while it's in
              an active phase (past kickoff, not yet in finalization). It drops off once you schedule the remaining
              hours or it moves into review. Use the “?” above for the full rules.
            </div>
          ) : (
            backlog.map(j => {
              const typeColor = JOB_TYPE_COLORS[j.ServiceDesc] || JOB_TYPE_COLORS[j.JobType] || { bg: '#E5E7EB', text: '#374151' };
              const budget = j.budget || 1;
              const myPct = Math.min(100, Math.round((j.myWip / budget) * 100));
              const othersPct = Math.min(100 - myPct, Math.round((j.othersWip / budget) * 100));
              const schedPct = Math.min(100 - myPct - othersPct, Math.round((j.scheduledTotal / budget) * 100));
              return (
                <div key={j.JobId} className="sched-staff-backlog-card" onClick={() => setBarTooltip(null)}>
                  <div className="sched-staff-backlog-header">
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                        <span className="sched-type-tag" style={{ background: typeColor.bg, color: typeColor.text }}>
                          {j.ServiceDesc || j.JobType || 'Other'}
                        </span>
                        <span style={{ fontSize: 11, color: '#6B7280' }}>
                          {j.StatusDetail || j.Status || 'Not Started'}
                        </span>
                        {myTimeJobIds.has(j.JobId) && (
                          <span className="sched-backlog-crosstag" title="You've also logged recent time on this job — it's in your My Time backlog too.">↔ Also in My Time</span>
                        )}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', cursor: 'pointer' }} className="sched-mgr-client-link"
                        onClick={() => navigate(`/schedule/client/${j.ClientId}`)}>
                        {j.ClientName}
                        {j.ClientStatus === 'FINANCIAL' && <span className="sched-client-status-badge sched-client-status-badge--financial" title="Client status: FINANCIAL (Stop Work)">⚠ Financial</span>}
                        {j.ClientStatus === 'LOST' && <span className="sched-client-status-badge sched-client-status-badge--lost" title="Client status: LOST">✕ Lost</span>}
                      </div>
                      <div style={{ fontSize: 12, color: '#6B7280' }}>{j.Name}</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      {j.overBudget ? (
                        <>
                          <div style={{ fontSize: 22, fontWeight: 800, color: '#DC2626' }}>+{j.overage}h</div>
                          <div style={{ fontSize: 10, color: '#DC2626', fontWeight: 600 }}>over budget</div>
                        </>
                      ) : (
                        <>
                          <div style={{ fontSize: 22, fontWeight: 800, color: BRAND.deepTeal }}>{j.remaining}h</div>
                          <div style={{ fontSize: 10, color: '#9CA3AF' }}>remaining</div>
                        </>
                      )}
                    </div>
                  </div>

                  <div style={{ margin: '10px 0 6px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 11, color: '#6B7280' }}>
                        {j.myWip}h you + {j.othersWip}h others logged | {j.scheduledTotal}h scheduled
                        {j.overBudget
                          ? <span style={{ color: '#DC2626', fontWeight: 600 }}> | +{j.overage}h over budget</span>
                          : ` | ${j.remaining}h remaining`}
                      </span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: j.pctComplete > 100 ? '#DC2626' : '#374151' }}>{j.pctComplete}%</span>
                    </div>
                    {(() => {
                      const isActive = seg => barTooltip?.jobId === j.JobId && barTooltip.segment === seg;
                      const toggle = (seg, e) => { e.stopPropagation(); setBarTooltip(prev => prev?.jobId === j.JobId && prev.segment === seg ? null : { jobId: j.JobId, segment: seg }); };
                      const segStyle = (bg, seg) => ({
                        background: bg, cursor: 'pointer', position: 'relative',
                        outline: isActive(seg) ? '2px solid #fff' : 'none',
                        boxShadow: isActive(seg) ? `0 0 0 3px ${bg}` : 'none',
                        zIndex: isActive(seg) ? 2 : 1,
                        borderRadius: isActive(seg) ? 2 : 0,
                      });
                      const tipStyle = (align) => ({
                        position: 'absolute', bottom: 18, [align]: 0,
                        background: '#1F2937', color: '#fff', borderRadius: 8,
                        padding: '10px 14px', fontSize: 11, whiteSpace: 'nowrap',
                        zIndex: 100, boxShadow: '0 4px 16px rgba(0,0,0,0.25)', minWidth: 150,
                        pointerEvents: 'auto',
                      });
                      return (
                        <div style={{ position: 'relative' }}>
                          <div style={{ display: 'flex', height: 12, background: '#E5E7EB', borderRadius: 6, overflow: 'visible' }}>
                            {myPct > 0 && (
                              <div style={{ width: `${myPct}%`, ...segStyle(BRAND.deepTeal, 'my') }} onClick={e => toggle('my', e)}>
                                {isActive('my') && (
                                  <div style={tipStyle('left')}>
                                    <div style={{ fontWeight: 700, marginBottom: 2, color: '#A7F3D0' }}>Your WIP</div>
                                    <div>{j.myWip}h logged</div>
                                  </div>
                                )}
                              </div>
                            )}
                            {othersPct > 0 && (
                              <div style={{ width: `${othersPct}%`, ...segStyle(BRAND.oceanBlue, 'others') }} onClick={e => toggle('others', e)}>
                                {isActive('others') && (
                                  <div style={tipStyle('left')}>
                                    <div style={{ fontWeight: 700, marginBottom: 4, color: '#93C5FD' }}>Others' WIP ({j.othersWip}h)</div>
                                    {j.wipByStaff.map((s, i) => (
                                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '1px 0' }}>
                                        <span>{s.name}</span>
                                        <span style={{ fontWeight: 600 }}>{Math.round(s.hours * 100) / 100}h</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                            {schedPct > 0 && (
                              <div style={{ width: `${schedPct}%`, ...segStyle(BRAND.sageGreen, 'sched') }} onClick={e => toggle('sched', e)}>
                                {isActive('sched') && (
                                  <div style={tipStyle('right')}>
                                    <div style={{ fontWeight: 700, marginBottom: 4, color: '#BBF7D0' }}>Scheduled ({j.scheduledTotal}h)</div>
                                    {j.scheduledByStaff.map((s, i) => (
                                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '1px 0' }}>
                                        <span>{s.name}</span>
                                        <span style={{ fontWeight: 600 }}>{Math.round(s.hours * 100) / 100}h</span>
                                      </div>
                                    ))}
                                    {j.scheduledByStaff.length === 0 && <div style={{ color: '#9CA3AF', fontStyle: 'italic' }}>No entries</div>}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                    <div style={{ display: 'flex', alignItems: 'center', marginTop: 5 }}>
                      <div style={{ display: 'flex', gap: 12, fontSize: 10, color: '#9CA3AF' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                          <span style={{ width: 8, height: 8, borderRadius: 2, background: BRAND.deepTeal }} /> My WIP
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                          <span style={{ width: 8, height: 8, borderRadius: 2, background: BRAND.oceanBlue }} /> Others' WIP
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                          <span style={{ width: 8, height: 8, borderRadius: 2, background: BRAND.sageGreen }} /> Scheduled
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                          <span style={{ width: 8, height: 8, borderRadius: 2, background: '#E5E7EB' }} /> Remaining
                        </span>
                      </div>
                      <span style={{ marginLeft: 'auto', fontSize: 10, color: '#6B7280' }}>Click a progress bar section for more details</span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 16, fontSize: 11, color: '#6B7280', marginTop: 4 }}>
                    <span>Budget: {j.budget}h</span>
                    {j.Deadline && <span>IDD: {new Date(typeof j.Deadline === 'string' ? j.Deadline.slice(0, 10) + 'T12:00:00' : j.Deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                    {j.ManagerName && <span>JM: {j.ManagerName}</span>}
                    {j.PartnerName && <span>JP: {j.PartnerName}</span>}
                  </div>

                  {j.overBudget ? (
                    <div style={{ marginTop: 8, padding: '6px 10px', background: '#FEF2F2', borderRadius: 6, fontSize: 12, color: '#DC2626', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 14 }}>&#9888;&#65039;</span>
                      Over budget by {j.overage}h — logged + scheduled hours exceed the {j.budget}h budget
                    </div>
                  ) : (
                    <div style={{ marginTop: 8, padding: '6px 10px', background: '#EFF6FF', borderRadius: 6, fontSize: 12, color: '#1D4ED8', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 14 }}>&#128161;</span>
                      ~{j.remaining}h remaining to schedule for this engagement
                    </div>
                  )}

                  <button className="sched-assign-btn" style={{ marginTop: 10, width: '100%' }}
                    onClick={() => openChooser({ ...j, dept: jobDept(j.ServiceDesc) }, { variant: 'myjobs', origin: 'backlog', subTab: 'myjobs' })}>
                    Schedule &rarr;
                  </button>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ── My Time backlog (everyone, WIP-driven) ── */}
      {effectiveSub === 'mytime' && (
        <div className="sched-staff-weeks">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <span ref={myTimeHelpRef} style={{ position: 'relative', display: 'inline-flex' }}>
                <button className="idd-help-btn" aria-label="Why do jobs appear in My Time?"
                  title="Why do jobs appear here?" onClick={() => setMyTimeHelpOpen(o => !o)}>?</button>
                {myTimeHelpOpen && (
                  <div className="sched-backlog-help-popover">
                    <div className="sched-backlog-help-title">Why a job shows in My Time</div>
                    <p>A job appears here when all of these are true:</p>
                    <ul>
                      <li>You've <strong>logged time</strong> on it in the last <strong>{recentDays} days</strong>.</li>
                      <li>The job still has <strong>budget remaining</strong> (budget minus everyone's logged hours minus hours already scheduled).</li>
                      <li>It's in an <strong>active phase</strong> (past kickoff, not yet in finalization).</li>
                      <li>You have <strong>no upcoming scheduled time</strong> on it — work you touched but aren't set to continue.</li>
                    </ul>
                    <p className="sched-backlog-help-foot">Use the 7/14/30-day buttons above to widen or narrow the window. Time entries under 1h aren't tracked here.</p>
                  </div>
                )}
              </span>
              <span style={{ fontSize: 12, color: '#6B7280' }}>Jobs you logged time on recently that still need work and aren't on your schedule.</span>
            </div>
            <button className="sched-assign-btn"
              title="Manually schedule against another job that doesn't show up in your My Time backlog."
              style={{ fontSize: 12, padding: '7px 14px', whiteSpace: 'nowrap', flexShrink: 0 }}
              onClick={scheduleAnotherJob}>
              + Schedule Another Job
            </button>
          </div>

          {myTime.length === 0 ? (
            <div className="sched-staff-empty">
              Nothing in My Time for the last {recentDays} days. A job shows here when you logged time on it recently,
              it still has budget remaining, it's in an active phase, and you have no upcoming scheduled time on it.
              Widen the window with the 7/14/30-day buttons above.
            </div>
          ) : (
            myTime.map(j => {
              const typeColor = JOB_TYPE_COLORS[j.ServiceDesc] || { bg: '#E5E7EB', text: '#374151' };
              return (
                <div key={j.JobId} className="sched-staff-backlog-card">
                  <div className="sched-staff-backlog-header">
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                        <span className="sched-type-tag" style={{ background: typeColor.bg, color: typeColor.text }}>
                          {j.ServiceDesc || 'Other'}
                        </span>
                        <span style={{ fontSize: 11, color: '#6B7280' }}>{j.StatusDetail || 'In Progress'}</span>
                        {myJobsJobIds.has(j.JobId) && (
                          <span className="sched-backlog-crosstag" title="You also lead this job (JM/JP) — it's in your My Jobs backlog too.">↔ Also in My Jobs</span>
                        )}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', cursor: 'pointer' }} className="sched-mgr-client-link"
                        onClick={() => j.ClientId && navigate(`/schedule/client/${j.ClientId}`)}>
                        {j.ClientName}
                      </div>
                      <div style={{ fontSize: 12, color: '#6B7280' }}>{j.Name}</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 22, fontWeight: 800, color: BRAND.deepTeal }}>{j.remaining}h</div>
                      <div style={{ fontSize: 10, color: '#9CA3AF' }}>remaining</div>
                    </div>
                  </div>

                  <div style={{ margin: '10px 0 6px' }}>
                    <JobProgressBar budget={j.budget} myWip={j.myWip} othersWip={j.othersWip}
                      wipTotal={j.wipTotal} scheduledTotal={j.scheduledTotal} remaining={j.remaining}
                      placeholderHours={j.placeholderHrs}
                      pctComplete={j.pctComplete} wipByStaff={j.wipByStaff} scheduledByStaff={j.scheduledByStaff}
                      showHeader showLegend tipDirection="up" />
                  </div>

                  <div style={{ display: 'flex', gap: 16, fontSize: 11, color: '#6B7280', marginTop: 4, flexWrap: 'wrap' }}>
                    <span>Budget: {j.budget}h</span>
                    <span>You logged: {j.myWip}h</span>
                    {j.Deadline && <span>IDD: {new Date((typeof j.Deadline === 'string' ? j.Deadline.slice(0, 10) : toISO(new Date(j.Deadline))) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                    {j.ManagerName && <span>JM: {j.ManagerName}</span>}
                    {j.PartnerName && <span>JP: {j.PartnerName}</span>}
                  </div>

                  <button className="sched-assign-btn" style={{ marginTop: 10, width: '100%' }}
                    onClick={() => openChooser({ ...j, dept: jobDept(j.ServiceDesc) }, { variant: 'mytime', origin: 'backlog', subTab: 'mytime' })}>
                    Schedule &rarr;
                  </button>
                </div>
              );
            })
          )}
        </div>
      )}

    </>
  );
}
